/**
 * Original compact SVG icons for Sprint 3 Training task cards.
 * Educational identification aids only — not official NZTA artwork copies.
 * NZ left-side driving / RHD context for road-layout diagrams.
 */
export default function TaskVisualIcon({ kind = "generic", className = "", title }) {
  const common = {
    viewBox: "0 0 40 40",
    width: "28",
    height: "28",
    className,
    "aria-hidden": title ? undefined : true,
    role: title ? "img" : undefined,
    focusable: "false",
  };

  const stroke = "currentColor";
  const fill = "none";
  const sw = 1.75;

  switch (kind) {
    case "stopSign":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <polygon
            points="12,4 28,4 36,12 36,28 28,36 12,36 4,28 4,12"
            fill="#c62828"
            stroke="#7f1d1d"
            strokeWidth="1"
          />
          <text x="20" y="24" textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">
            STOP
          </text>
        </svg>
      );
    case "giveWay":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <polygon points="20,6 34,34 6,34" fill="#fff" stroke="#c62828" strokeWidth="2.2" />
          <text x="20" y="30" textAnchor="middle" fontSize="5.5" fontWeight="700" fill="#c62828">
            GIVE WAY
          </text>
        </svg>
      );
    case "roundabout":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <circle cx="20" cy="20" r="7" stroke={stroke} strokeWidth={sw} fill={fill} />
          <circle cx="20" cy="20" r="2.2" fill={stroke} />
          <path d="M20 8v4M20 28v4M8 20h4M28 20h4" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
          <path d="M14 11c3-3 9-3 12 0" stroke={stroke} strokeWidth={sw} fill={fill} markerEnd="url(#arr)" />
          <path d="M26 14l2-4 3 2" stroke={stroke} strokeWidth={sw} fill={fill} />
        </svg>
      );
    case "railway":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M8 28h24" stroke={stroke} strokeWidth={sw} />
          <path d="M12 28V14h16v14" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M12 18h16M12 22h16M12 26h16" stroke={stroke} strokeWidth="1.2" />
          <path d="M10 10l20 16M30 10L10 26" stroke="#c62828" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case "flushMedian":
      // Overhead: NZ left-side — vehicle enters central flush median for right turn
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <rect x="4" y="6" width="32" height="28" rx="1" stroke={stroke} strokeWidth="1.2" fill={fill} />
          <path d="M4 16h32M4 24h32" stroke={stroke} strokeWidth="1" strokeDasharray="2 2" opacity="0.5" />
          <rect x="17" y="14" width="6" height="12" fill="#94a3b8" opacity="0.55" />
          <rect x="8" y="25" width="7" height="4" rx="0.8" fill={stroke} />
          <path d="M15 27h5c2 0 4-2 4-4v-3" stroke="#2563eb" strokeWidth="1.8" fill={fill} />
          <path d="M22 18l3 2-1 3" stroke="#2563eb" strokeWidth="1.5" fill={fill} />
        </svg>
      );
    case "uTurn":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path
            d="M12 30V16a8 8 0 0 1 16 0v6"
            stroke={stroke}
            strokeWidth={sw}
            fill={fill}
            strokeLinecap="round"
          />
          <path d="M24 18l4 4-4 4" stroke={stroke} strokeWidth={sw} fill={fill} strokeLinejoin="round" />
        </svg>
      );
    case "movingOff":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M6 30h28" stroke={stroke} strokeWidth={sw} />
          <rect x="8" y="20" width="12" height="7" rx="1.2" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M22 23h8l2 4H22z" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M28 16l6 4-6 4" stroke="#2563eb" strokeWidth={sw} fill={fill} />
        </svg>
      );
    case "pullingOver":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M6 30h28" stroke={stroke} strokeWidth={sw} />
          <path d="M8 12v18" stroke={stroke} strokeWidth="1.2" strokeDasharray="2 2" opacity="0.6" />
          <rect x="14" y="16" width="12" height="7" rx="1.2" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M18 12l-6 6 2 1" stroke="#2563eb" strokeWidth={sw} fill={fill} />
        </svg>
      );
    case "smoothStop":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M6 28h28" stroke={stroke} strokeWidth={sw} />
          <rect x="14" y="18" width="12" height="7" rx="1.2" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M10 14h8" stroke="#2563eb" strokeWidth={sw} strokeLinecap="round" />
          <path d="M18 14l-3-2M18 14l-3 2" stroke="#2563eb" strokeWidth={sw} fill={fill} />
        </svg>
      );
    case "mirrors":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <ellipse cx="20" cy="20" rx="10" ry="12" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M14 18h12M16 24h8" stroke={stroke} strokeWidth="1.3" opacity="0.7" />
          <circle cx="20" cy="12" r="1.5" fill={stroke} />
        </svg>
      );
    case "threeChecks":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M10 20h6M24 20h6M20 10v6M20 24v6" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
          <circle cx="20" cy="20" r="3" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M8 12l3 3M32 12l-3 3M8 28l3-3M32 28l-3-3" stroke={stroke} strokeWidth="1.4" />
        </svg>
      );
    case "scanning":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <circle cx="20" cy="20" r="8" stroke={stroke} strokeWidth={sw} fill={fill} />
          <circle cx="20" cy="20" r="3" fill={stroke} />
          <path d="M20 6v4M20 30v4M6 20h4M30 20h4" stroke={stroke} strokeWidth="1.4" />
        </svg>
      );
    case "lanePosition":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M12 6v28M28 6v28" stroke={stroke} strokeWidth="1.3" />
          <path d="M20 8v24" stroke={stroke} strokeWidth="1.2" strokeDasharray="3 3" />
          <rect x="15" y="16" width="10" height="8" rx="1" fill={stroke} />
        </svg>
      );
    case "laneChange":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M10 6v28M30 6v28" stroke={stroke} strokeWidth="1.3" />
          <path d="M20 8v24" stroke={stroke} strokeWidth="1.2" strokeDasharray="3 3" />
          <rect x="8" y="18" width="8" height="5" rx="0.8" fill={stroke} opacity="0.45" />
          <rect x="22" y="18" width="8" height="5" rx="0.8" fill={stroke} />
          <path d="M16 20h6" stroke="#2563eb" strokeWidth={sw} />
          <path d="M20 17l4 3-4 3" stroke="#2563eb" strokeWidth={sw} fill={fill} />
        </svg>
      );
    case "parkedVehicles":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M6 30h28" stroke={stroke} strokeWidth={sw} />
          <rect x="7" y="20" width="9" height="6" rx="1" stroke={stroke} strokeWidth="1.3" fill={fill} />
          <rect x="24" y="20" width="9" height="6" rx="1" stroke={stroke} strokeWidth="1.3" fill={fill} />
          <path d="M12 14c4-6 12-6 16 0" stroke="#2563eb" strokeWidth={sw} fill={fill} />
        </svg>
      );
    case "meetingTraffic":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M14 6v28M26 6v28" stroke={stroke} strokeWidth="1.3" />
          <rect x="8" y="10" width="7" height="5" rx="0.8" fill={stroke} />
          <rect x="25" y="24" width="7" height="5" rx="0.8" fill={stroke} />
          <path d="M18 18h4M18 18l2-2M18 18l2 2" stroke="#c62828" strokeWidth="1.4" fill={fill} />
        </svg>
      );
    case "gapSelection":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <rect x="6" y="14" width="8" height="5" rx="0.8" fill={stroke} opacity="0.5" />
          <rect x="26" y="14" width="8" height="5" rx="0.8" fill={stroke} opacity="0.5" />
          <rect x="16" y="22" width="8" height="5" rx="0.8" fill={stroke} />
          <path d="M20 12v8" stroke="#2563eb" strokeWidth={sw} strokeDasharray="2 2" />
        </svg>
      );
    case "trafficLights":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <rect x="14" y="6" width="12" height="28" rx="2" stroke={stroke} strokeWidth={sw} fill={fill} />
          <circle cx="20" cy="13" r="3" fill="#c62828" />
          <circle cx="20" cy="20" r="3" fill="#ca8a04" />
          <circle cx="20" cy="27" r="3" fill="#16a34a" />
        </svg>
      );
    case "intersection":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M18 4v32M22 4v32M4 18h32M4 22h32" stroke={stroke} strokeWidth="1.4" />
        </svg>
      );
    case "t2":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <rect x="8" y="8" width="24" height="24" rx="2" stroke={stroke} strokeWidth={sw} fill={fill} />
          <text x="20" y="25" textAnchor="middle" fontSize="12" fontWeight="700" fill={stroke}>
            T2
          </text>
        </svg>
      );
    case "t3":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <rect x="8" y="8" width="24" height="24" rx="2" stroke={stroke} strokeWidth={sw} fill={fill} />
          <text x="20" y="25" textAnchor="middle" fontSize="12" fontWeight="700" fill={stroke}>
            T3
          </text>
        </svg>
      );
    case "busLane":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <rect x="8" y="12" width="24" height="14" rx="2" stroke={stroke} strokeWidth={sw} fill={fill} />
          <text x="20" y="22" textAnchor="middle" fontSize="7" fontWeight="700" fill={stroke}>
            BUS
          </text>
        </svg>
      );
    case "parallelPark":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M6 30h28" stroke={stroke} strokeWidth={sw} />
          <rect x="6" y="18" width="9" height="6" rx="1" stroke={stroke} strokeWidth="1.2" fill={fill} />
          <rect x="25" y="18" width="9" height="6" rx="1" stroke={stroke} strokeWidth="1.2" fill={fill} />
          <rect x="16" y="12" width="8" height="5" rx="0.8" fill={stroke} />
          <path d="M20 18v5" stroke="#2563eb" strokeWidth={sw} />
          <path d="M17 21h6" stroke="#2563eb" strokeWidth="1.3" />
        </svg>
      );
    case "bayPark":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M8 10v20M16 10v20M24 10v20M32 10v20" stroke={stroke} strokeWidth="1.2" />
          <path d="M8 30h24" stroke={stroke} strokeWidth={sw} />
          <rect x="17" y="14" width="6" height="10" rx="0.8" fill={stroke} />
        </svg>
      );
    case "reversing":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <rect x="14" y="12" width="12" height="8" rx="1.2" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M20 22v8" stroke="#2563eb" strokeWidth={sw} />
          <path d="M16 28l4 4 4-4" stroke="#2563eb" strokeWidth={sw} fill={fill} />
        </svg>
      );
    case "threePoint":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M8 28h24" stroke={stroke} strokeWidth={sw} />
          <path d="M10 24c4-10 16-10 20 0" stroke="#2563eb" strokeWidth={sw} fill={fill} />
          <path d="M26 18c-2 6-8 8-14 6" stroke="#16a34a" strokeWidth={sw} fill={fill} />
          <path d="M12 24h8" stroke="#c62828" strokeWidth={sw} />
          <circle cx="12" cy="24" r="1.5" fill="#c62828" />
          <circle cx="28" cy="24" r="1.5" fill="#2563eb" />
          <circle cx="16" cy="20" r="1.5" fill="#16a34a" />
        </svg>
      );
    case "following":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <rect x="14" y="6" width="12" height="7" rx="1" stroke={stroke} strokeWidth={sw} fill={fill} />
          <rect x="14" y="26" width="12" height="7" rx="1" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M20 14v10" stroke="#2563eb" strokeWidth={sw} strokeDasharray="2 2" />
        </svg>
      );
    case "speed":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <circle cx="20" cy="22" r="12" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M12 22a8 8 0 0 1 16 0" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M20 22l6-6" stroke="#c62828" strokeWidth={sw} strokeLinecap="round" />
        </svg>
      );
    case "merging":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M10 34V10c0-2 2-4 6-4h4" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M30 34V18c0-4-2-6-6-6h-4" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M18 16h6" stroke="#2563eb" strokeWidth={sw} />
          <path d="M22 13l4 3-4 3" stroke="#2563eb" strokeWidth={sw} fill={fill} />
        </svg>
      );
    case "motorway":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M8 8v24M32 8v24" stroke={stroke} strokeWidth="1.4" />
          <path d="M20 8v24" stroke={stroke} strokeWidth="1.3" strokeDasharray="3 3" />
          <path d="M12 14h4M24 20h4M12 26h4" stroke={stroke} strokeWidth="1.5" />
        </svg>
      );
    case "openRoad":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M8 30c8-16 16-16 24 0" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M20 14v10" stroke={stroke} strokeWidth="1.3" strokeDasharray="2 2" />
          <rect x="16" y="22" width="8" height="5" rx="0.8" fill={stroke} />
        </svg>
      );
    case "pedestrian":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <circle cx="20" cy="10" r="3" fill={stroke} />
          <path d="M20 14v10M14 18h12M16 34l4-10 4 10" stroke={stroke} strokeWidth={sw} fill={fill} strokeLinejoin="round" />
        </svg>
      );
    case "cyclist":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <circle cx="12" cy="28" r="5" stroke={stroke} strokeWidth={sw} fill={fill} />
          <circle cx="28" cy="28" r="5" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M12 28h8l4-10h4" stroke={stroke} strokeWidth={sw} fill={fill} />
          <circle cx="24" cy="12" r="2.5" fill={stroke} />
        </svg>
      );
    case "meetingPoints":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M14 6v28M26 6v28" stroke={stroke} strokeWidth="1.3" />
          <rect x="8" y="26" width="7" height="5" rx="0.8" fill={stroke} />
          <rect x="25" y="9" width="7" height="5" rx="0.8" fill={stroke} />
          <path d="M18 20h4" stroke="#16a34a" strokeWidth="1.6" strokeLinecap="round" />
          <circle cx="20" cy="20" r="2" fill="none" stroke="#16a34a" strokeWidth="1.4" />
        </svg>
      );
    case "divergentMovement":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M20 32V18" stroke={stroke} strokeWidth={sw} />
          <path d="M20 18c0-6-6-8-10-10" stroke="#2563eb" strokeWidth={sw} fill={fill} />
          <path d="M20 18c0-6 6-8 10-10" stroke="#c62828" strokeWidth={sw} fill={fill} />
          <path d="M8 10l2-2 1 3" stroke="#2563eb" strokeWidth={sw} fill={fill} />
          <path d="M32 10l-2-2-1 3" stroke="#c62828" strokeWidth={sw} fill={fill} />
        </svg>
      );
    case "openJunction":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M18 4v14M22 4v14M4 18h14M22 18h14" stroke={stroke} strokeWidth="1.4" />
          <path d="M14 8l6-4 6 4M8 14l4-6 4 6" stroke="#16a34a" strokeWidth="1.2" fill={fill} opacity="0.7" />
          <rect x="17" y="17" width="6" height="6" fill={stroke} opacity="0.5" />
        </svg>
      );
    case "closedJunction":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path d="M18 4v14M22 4v14M4 18h14M22 18h14" stroke={stroke} strokeWidth="1.4" />
          <rect x="4" y="4" width="14" height="14" fill={stroke} opacity="0.35" />
          <rect x="17" y="17" width="6" height="6" fill={stroke} opacity="0.5" />
          <path d="M9 9l4 4" stroke="#c62828" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case "hazard":
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <polygon points="20,6 34,34 6,34" fill="#ca8a04" stroke="#854d0e" strokeWidth="1.2" />
          <text x="20" y="28" textAnchor="middle" fontSize="14" fontWeight="700" fill="#fff">
            !
          </text>
        </svg>
      );
    default:
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <circle cx="20" cy="20" r="10" stroke={stroke} strokeWidth={sw} fill={fill} />
          <path d="M14 20h12M20 14v12" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
        </svg>
      );
  }
}
